import { useMemo } from "react";
import {
  Box,
  Typography,
  Chip,
  Paper,
  Slider,
} from "@mui/material";
import {
  WbSunny,
  WbTwilight,
  Brightness4,
  AccessTime,
} from "@mui/icons-material";
import TimeCard from "./TimeCard";
import {
  startTimeToMinutes,
  endTimeToMinutes,
  minutesToTime,
  formatDuration,
} from "../../utils/timeHelpers";

interface TimeRangeSelectorProps {
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
  onStartTimeChange: (value: string) => void;
  onEndTimeChange: (value: string) => void;
  minTime?: string; // Shop opening time (HH:mm)
  maxTime?: string; // Shop closing time (HH:mm) - can be after midnight
  step?: number; // Step for minutes (default: 30)
  format?: "12h" | "24h"; // Time display format (default: "24h")
  showPresets?: boolean; // Show quick presets (default: true)
  showSlider?: boolean; // Show slider (default: false)
  showDuration?: boolean; // Show duration display (default: true)
}

export default function TimeRangeSelector({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  minTime = "00:00",
  maxTime = "23:59",
  step = 30,
  format = "24h",
  showPresets = true,
  showSlider = false,
  showDuration = true,
}: TimeRangeSelectorProps) {
  // Use the utility functions that handle overnight properly
  const minMinutes = startTimeToMinutes(minTime);
  const maxMinutes = endTimeToMinutes(maxTime);
  const startMinutes = startTimeToMinutes(startTime);
  const endMinutes = endTimeToMinutes(endTime);

  // Calculate duration
  const duration = endMinutes - startMinutes;

  // Presets based on shop hours
  const presets = useMemo(() => {
    const shopDuration = maxMinutes - minMinutes;
    const midpoint = minMinutes + Math.floor(shopDuration / 2);
    // Round midpoint to nearest step
    const roundedMidpoint = Math.round(midpoint / step) * step;

    const eveningStart = startTimeToMinutes("17:00");

    return [
      {
        label: "Full Day",
        icon: <AccessTime fontSize="small" />,
        start: minMinutes,
        end: maxMinutes,
      },
      {
        label: "Morning",
        icon: <WbSunny fontSize="small" />,
        start: minMinutes,
        end: roundedMidpoint,
      },
      {
        label: "Afternoon",
        icon: <WbTwilight fontSize="small" />,
        start: roundedMidpoint,
        end: maxMinutes,
      },
      {
        label: "Evening",
        icon: <Brightness4 fontSize="small" />,
        start: Math.max(minMinutes, eveningStart),
        end: maxMinutes,
      },
    ].filter(
      (preset) =>
        preset.start >= minMinutes &&
        preset.end <= maxMinutes &&
        preset.end > preset.start
    );
  }, [minMinutes, maxMinutes, step]);

  // Handle preset selection
  const handlePreset = (start: number, end: number) => {
    onStartTimeChange(minutesToTime(start));
    onEndTimeChange(minutesToTime(end));
  };

  // Handle slider change
  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      onStartTimeChange(minutesToTime(newValue[0]));
      onEndTimeChange(minutesToTime(newValue[1]));
    }
  };

  // Generate marks for slider
  const sliderMarks = useMemo(() => {
    const marks = [];
    for (let m = minMinutes; m <= maxMinutes; m += 60) {
      const hours = Math.floor((m % 1440) / 60);
      marks.push({
        value: m,
        label: format === "24h" ? `${hours}:00` : `${hours % 12 || 12}${hours >= 12 ? "p" : "a"}`,
      });
    }
    return marks;
  }, [minMinutes, maxMinutes, format]);

  // Check if current selection matches a preset
  const activePreset = presets.find(
    (p) => p.start === startMinutes && p.end === endMinutes
  );

  // Calculate maxTime for start card (must be before end time minus step)
  const startMaxMinutes = Math.max(minMinutes, endMinutes - step);
  // Calculate minTime for end card (must be after start time plus step)
  const endMinMinutes = Math.min(maxMinutes, startMinutes + step);

  return (
    <Box sx={{ width: "100%" }}>
      {/* Quick Presets */}
      {showPresets && presets.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            QUICK SELECT
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {presets.map((preset) => (
              <Chip
                key={preset.label}
                icon={preset.icon}
                label={preset.label}
                onClick={() => handlePreset(preset.start, preset.end)}
                color={activePreset?.label === preset.label ? "primary" : "default"}
                variant={activePreset?.label === preset.label ? "filled" : "outlined"}
                sx={{
                  transition: "all 0.2s",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: 1,
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Visual Time Range Slider */}
      {showSlider && (
        <Box sx={{ mb: 3, px: 1 }}>
          <Slider
            value={[startMinutes, endMinutes]}
            onChange={handleSliderChange}
            min={minMinutes}
            max={maxMinutes}
            step={step}
            marks={sliderMarks}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => minutesToTime(value)}
            sx={{
              "& .MuiSlider-thumb": { width: 20, height: 20 },
              "& .MuiSlider-track": { height: 8, borderRadius: 4 },
              "& .MuiSlider-rail": { height: 8, borderRadius: 4, opacity: 0.3 },
              "& .MuiSlider-mark": { height: 12, width: 2, bgcolor: "divider" },
              "& .MuiSlider-markLabel": { fontSize: "0.7rem", color: "text.secondary" },
            }}
          />
        </Box>
      )}

      {/* Time Cards Row */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {/* Start Time Card */}
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <TimeCard
            label="Start Time"
            value={startTime}
            onChange={onStartTimeChange}
            minTime={minTime}
            maxTime={minutesToTime(startMaxMinutes)}
            step={step}
            format={format}
            color="primary"
          />
        </Box>

        {/* Duration Display */}
        {showDuration && (
          <Paper
            elevation={0}
            sx={{
              py: 1.5,
              px: 2,
              borderRadius: 2,
              bgcolor: "primary.50",
              border: "1px solid",
              borderColor: "primary.200",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 90,
            }}
          >
            <Typography variant="caption" color="primary.main" fontWeight={500}>
              Duration
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {duration > 0 ? formatDuration(duration) : "0h"}
            </Typography>
          </Paper>
        )}

        {/* End Time Card */}
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <TimeCard
            label="End Time"
            value={endTime}
            onChange={onEndTimeChange}
            minTime={minutesToTime(endMinMinutes)}
            maxTime={maxTime}
            step={step}
            format={format}
            color="primary"
          />
        </Box>
      </Box>
    </Box>
  );
}
